import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Collapse,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  AvatarGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PeopleIcon from '@mui/icons-material/People';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { EmployeeAvatar } from '../EmployeeAvatar';
import TechnologyFilter from '../TechnologyFilter';
import { radarApi } from '../../../services/api/radarApi';

const USAGE_GROUPS = [
  {
    key: 'employeeRadar',
    label: 'Employee Radars',
    icon: PeopleIcon,
    getCount: (u) => u?.employeeRadar?.count || 0,
    getSample: (u) => u?.employeeRadar?.sample || [],
    renderItem: (item) => ({
      primary: item.employeeName,
      secondary: item.competencyLevel,
      avatar: { azureId: item.azureId, fullName: item.employeeName },
    }),
    unit: 'individuals',
  },
  {
    key: 'projectRadar',
    label: 'Project Tech Stacks',
    icon: FolderIcon,
    getCount: (u) => u?.projectRadar?.count || 0,
    getSample: (u) => u?.projectRadar?.sample || [],
    renderItem: (item) => ({
      primary: item.projectName,
      secondary: null,
    }),
    unit: 'projects',
  },
  {
    key: 'cvGeneralSkills',
    label: 'CV — General Skills',
    icon: DescriptionIcon,
    getCount: (u) => u?.employeeCVs?.generalSkills?.count || 0,
    getSample: (u) => u?.employeeCVs?.generalSkills?.sample || [],
    renderItem: (item) => ({
      primary: item.employeeName,
      secondary: item.competencyLevel,
      avatar: { azureId: item.azureId, fullName: item.employeeName },
    }),
    unit: 'individuals',
  },
  {
    key: 'cvEmploymentHistory',
    label: 'CV — Employment History',
    icon: DescriptionIcon,
    getCount: (u) => u?.employeeCVs?.employmentHistorySkills?.count || 0,
    getSample: (u) => u?.employeeCVs?.employmentHistorySkills?.sample || [],
    renderItem: (item) => ({
      primary: item.employeeName,
      secondary: item.companyName ? `Employer: ${item.companyName}` : null,
      avatar: { azureId: item.azureId, fullName: item.employeeName },
    }),
    unit: 'entries',
  },
  {
    key: 'dashboards',
    label: 'Dashboards',
    icon: DashboardIcon,
    getCount: (u) => u?.dashboards?.count || 0,
    getSample: (u) => u?.dashboards?.sample || [],
    renderItem: (item) => ({
      primary: item.dashboardName,
      secondary: item.projectName ? `Project: ${item.projectName}` : null,
    }),
    unit: 'dashboards',
  },
  {
    key: 'skillsMatrix',
    label: 'Skills Matrix',
    icon: AssessmentIcon,
    getCount: (u) => (u?.skillsMatrix?.templates?.count || 0) + (u?.skillsMatrix?.responses?.count || 0),
    getSample: (u) => u?.skillsMatrix?.templates?.sample || [],
    renderItem: (item) => ({
      primary: item.templateName,
      secondary: null,
    }),
    unit: 'mappings',
  },
];

const UsageGroup = ({ group, usage }) => {
  const [expanded, setExpanded] = useState(false);
  const count = group.getCount(usage);
  const sample = group.getSample(usage);
  const Icon = group.icon;

  if (count === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 0.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 0.75,
          px: 1.5,
          borderRadius: 1,
          cursor: sample.length > 0 ? 'pointer' : 'default',
          '&:hover': sample.length > 0 ? { bgcolor: 'action.hover' } : {},
        }}
        onClick={() => {
          if (sample.length > 0) {
            setExpanded(!expanded);
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Icon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2">{group.label}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip
            label={`${count} ${group.unit}`}
            size="small"
            sx={{ height: 22, fontSize: '0.75rem' }}
          />
          {sample.length > 0 && (
            <IconButton size="small" sx={{ p: 0.25 }}>
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ pl: 5, pr: 1.5, pb: 1 }}>
          {sample.map((item, idx) => {
            const rendered = group.renderItem(item);
            return (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                {rendered.avatar && (
                  <EmployeeAvatar employee={rendered.avatar} size={24} />
                )}
                <Box>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {rendered.primary}
                  </Typography>
                  {rendered.secondary && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {rendered.secondary}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
          {count > sample.length && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              and {count - sample.length} more...
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const SafeDeleteDialog = ({ isOpen, onClose, onDeleted, segmentId, segmentName }) => {
  const [loading, setLoading] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [error, setError] = useState(null);
  const [action, setAction] = useState('replace');
  const [replacement, setReplacement] = useState([]);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch usage when dialog opens
  useEffect(() => {
    if (isOpen && segmentId) {
      setLoading(true);
      setError(null);
      setUsageData(null);
      setAction('replace');
      setReplacement([]);
      setConfirmText('');

      radarApi.getSegmentUsage(segmentId)
        .then((data) => {
          setUsageData(data);
          // If skills matrix is in use, ensure force-delete isn't selected
          const smCount = (data?.usage?.skillsMatrix?.templates?.count || 0) +
            (data?.usage?.skillsMatrix?.responses?.count || 0);
          if (smCount > 0) {
            setAction('replace');
          }
        })
        .catch((err) => {
          setError(err.message || 'Failed to load usage data');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isOpen, segmentId]);

  const handleConfirm = useCallback(async () => {
    setSubmitting(true);
    setError(null);

    try {
      if (!usageData?.isInUse) {
        // Not in use — simple delete, no action needed
        await radarApi.safeDeleteSegment(segmentId, {});
      } else if (action === 'replace') {
        await radarApi.safeDeleteSegment(segmentId, {
          action: 'replace',
          replacementLeafSegmentId: replacement[0]?.id,
        });
      } else {
        await radarApi.safeDeleteSegment(segmentId, {
          action: 'force-delete',
          confirmation: 'DELETE_ALL_REFERENCES',
        });
      }

      if (onDeleted) {
        onDeleted(segmentId);
      }
      onClose();
    } catch (err) {
      setError(err.data?.message || err.message || 'Failed to delete segment');
    } finally {
      setSubmitting(false);
    }
  }, [action, segmentId, replacement, onClose, onDeleted]);

  const isNotInUse = usageData && !usageData.isInUse;
  const hasSkillsMatrixUsage =
    (usageData?.usage?.skillsMatrix?.templates?.count || 0) +
    (usageData?.usage?.skillsMatrix?.responses?.count || 0) > 0;
  const canConfirmReplace = action === 'replace' && replacement.length > 0;
  const canConfirmForceDelete = action === 'force-delete' && confirmText === segmentName;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <WarningAmberIcon sx={{ color: 'warning.main' }} />
        <Typography variant="h6" component="span">
          Delete "{segmentName}"
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Loading state */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {/* Error state */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Not in use — simple confirmation */}
        {isNotInUse && (
          <Typography variant="body2" color="text.secondary">
            This technology is not used anywhere. It can be safely deleted.
          </Typography>
        )}

        {/* In use — show usage and options */}
        {usageData && usageData.isInUse && (
          <>
            {/* Usage summary */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                <strong>"{segmentName}"</strong> is currently used in:
              </Typography>
              <Box
                sx={{
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  py: 0.5,
                }}
              >
                {USAGE_GROUPS.map((group) => (
                  <UsageGroup key={group.key} group={group} usage={usageData.usage} />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Total: {usageData.totalReferences} references
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Action selection */}
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Choose an action:
            </Typography>

            <RadioGroup
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                setError(null);
              }}
            >
              {/* Replace option */}
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: action === 'replace' ? 'primary.main' : 'grey.300',
                  borderRadius: 1.5,
                  p: 2,
                  mb: 1.5,
                  bgcolor: action === 'replace' ? 'primary.50' : 'transparent',
                  transition: 'all 0.2s',
                }}
              >
                <FormControlLabel
                  value="replace"
                  control={<Radio size="small" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <SwapHorizIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Replace with another technology
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 0 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 3.5, mb: 1 }}>
                  All {usageData.totalReferences} references will be migrated to the replacement. Then the original will be deleted.
                  If a consultant or project already has the replacement technology, the duplicate will be removed and the higher competency level will be kept.
                </Typography>

                <Collapse in={action === 'replace'}>
                  <Box sx={{ ml: 3.5, mt: 1 }}>
                    <TechnologyFilter
                      value={replacement}
                      onChange={setReplacement}
                      singleSelect
                      excludeIds={usageData?.leafSegmentId ? [usageData.leafSegmentId] : []}
                      searchWidth={350}
                      sx={{ position: 'relative', zIndex: 1300 }}
                    />
                  </Box>
                </Collapse>
              </Box>

              {/* Force delete option */}
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: hasSkillsMatrixUsage
                    ? 'grey.200'
                    : action === 'force-delete' ? 'error.main' : 'grey.300',
                  borderRadius: 1.5,
                  p: 2,
                  bgcolor: hasSkillsMatrixUsage
                    ? 'grey.50'
                    : action === 'force-delete' ? 'error.50' : 'transparent',
                  opacity: hasSkillsMatrixUsage ? 0.6 : 1,
                  transition: 'all 0.2s',
                }}
              >
                <FormControlLabel
                  value="force-delete"
                  disabled={hasSkillsMatrixUsage}
                  control={<Radio size="small" color="error" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <DeleteForeverIcon sx={{ fontSize: 18, color: hasSkillsMatrixUsage ? 'text.disabled' : 'error.main' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Delete and remove from all locations
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 0 }}
                />
                {hasSkillsMatrixUsage ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 3.5 }}>
                    Not available — this technology is used in the skills matrix. Use "Replace" instead.
                  </Typography>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 3.5 }}>
                      Permanently removes "{segmentName}" from all {usageData.totalReferences} locations. This cannot be undone.
                    </Typography>

                    <Collapse in={action === 'force-delete'}>
                      <Box sx={{ ml: 3.5, mt: 1.5 }}>
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.75 }}>
                          Type <strong>{segmentName}</strong> to confirm:
                        </Typography>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder={segmentName}
                          value={confirmText}
                          onChange={(e) => setConfirmText(e.target.value)}
                          error={confirmText.length > 0 && confirmText !== segmentName}
                          sx={{ maxWidth: 350 }}
                        />
                      </Box>
                    </Collapse>
                  </>
                )}
              </Box>
            </RadioGroup>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>

        {/* Not in use — simple delete */}
        {isNotInUse && (
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            Delete
          </Button>
        )}

        {/* Replace action */}
        {usageData?.isInUse && action === 'replace' && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!canConfirmReplace || submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <SwapHorizIcon />}
          >
            Replace & Delete
          </Button>
        )}

        {/* Force delete action */}
        {usageData?.isInUse && action === 'force-delete' && (
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={!canConfirmForceDelete || submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <DeleteForeverIcon />}
          >
            Permanently Delete
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SafeDeleteDialog;
